<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import ThemeToggle from '@/components/ThemeToggle.vue'
import * as hosting from '@/api/hosting'
import * as ls from '@/api/ls'
import * as pluginMarket from '@/api/pluginMarket'
import * as reseller from '@/api/reseller'
import { useBackdropClose } from '@/lib/backdropClose'
import { isActiveJobState } from '@/lib/jobs'
import { canManageMarket, reviewQueueCount } from '@/lib/pluginMarket'
import { usePolling } from '@/lib/poll'
import { pendingReviews, setPendingReviews } from '@/lib/reviewBadge'
import { isCompact } from '@/lib/viewport'
import type { RoleName } from '@/stores/auth'
import { useAuthStore } from '@/stores/auth'

const auth = useAuthStore()
const router = useRouter()
const route = useRoute()
const logoSrc = import.meta.env.BASE_URL + 'assets/logo-tight.png'

const pendingAdmins = ref(0)
/** 代理商 realm 的申請清單（只有代理商登入時才載；我方員工打那支會 403）。 */
const myReqDraft = ref(0)
const myReqPending = ref(0)
/**
 * 「授權申請」徽章＝**這個角色現在該動手的筆數**（批93-C 開通流程）：
 * - superadmin＝待開通（pending）——最後開通是他的活
 * - **manager 以上**＝待開通（pending）——批110 起最後核發是營運主管的活（superadmin 收斂
 *   成治理角色），所以那顆「還有幾筆等我按」的紅點跟著下放；漏改的話新角色會看不到自己
 *   的待辦，而那正是他每天第一個要看的數字
 * - admin＝我方草稿（origin=admin 的 draft）——內容還沒確認送出，卡在我們手上
 * - reseller＝自己的草稿＋待我方開通（兩者都想知道進度）
 * operator 看不到這一項（整個「授權」群對他不可見）。
 */
const pendingReq = computed(() => {
  if (auth.isReseller) return myReqDraft.value + myReqPending.value
  return staffReq.value
})
/**
 * 我方員工的徽章數（批121 §9：改由**伺服器**算全表）。
 *
 * ★ 以前是 `store.loadRequests()` 載一頁 50 筆再在前端數——申請單超過一頁之後，
 *   第 51 筆起的待審核單在徽章上看不到，而審核者正是靠那個數字決定今天要不要處理。
 *   順帶省掉一次「為了數數而載 50 筆含 `request_json` 的列」。
 * ★ **刻意存在本地 ref 而不是 `store.requestCounts`**：那一份歸〈授權申請〉頁用，
 *   它會照該頁當下的過濾條件重載；共用一個 ref 會讓側欄徽章被頁面的過濾器改掉。
 */
const staffReq = ref(0)
/**
 * 代理商側欄〈授權申請〉的**「已核發 N」綠徽章**（批108 裁示 15）。
 *
 * 代理商送出申請之後沒有任何通知管道——他只能自己回頁面按重新整理，看我方核發了沒。
 * 這顆徽章就是那個通知：**`reviewed_at` 比「上次看過的時刻」新的 `approved` 筆數**，
 * 進〈授權申請〉頁就歸零。
 *
 * **刻意用 localStorage 而不是後端加一欄已讀水位**：這是一個人在一台機器上的閱讀狀態，
 * 換裝置重新亮一次無傷，不值得為它動 schema 與 API。
 * ★ **這一顆仍是近似**（只涵蓋已核發那個桶的第一頁，`limit: 200`）：判準是
 *   `reviewed_at` 比水位新，計數端點給不了這個。側欄其他徽章在批121 已改成伺服器
 *   算的全表數字，只有這顆留著——真的超過 200 筆未讀時，該做的是去頁面看，不是把
 *   紅點的數字算得更精確。
 */
const SEEN_KEY = 'ls.reseller.requests.lastSeenApproved'
const approvedUnseen = ref(0)

/**
 * 上次看過的時刻（epoch 毫秒）。**第一次讀不到就當場寫 now 並回 now**——否則新帳號一登入
 * 就會看到「已核發 37」（歷史全算未讀），那個數字沒有任何行動意義，只會教人忽略徽章。
 */
function readSeenAt(): number {
  try {
    const raw = localStorage.getItem(SEEN_KEY)
    const n = raw ? Number(raw) : Number.NaN
    if (Number.isFinite(n)) return n
    localStorage.setItem(SEEN_KEY, String(Date.now()))
  } catch {
    // 無痕視窗／停用儲存：徽章每次重新登入都從「現在」起算，功能不受影響。
  }
  return Date.now()
}
function markApprovedSeen() {
  try {
    localStorage.setItem(SEEN_KEY, String(Date.now()))
  } catch {
    /* 同上：存不進去就只是下次重算，不影響畫面 */
  }
  approvedUnseen.value = 0
}
/** ISO 時刻 → epoch 毫秒（解析不出來＝0，永遠不會被判成「新的」）。 */
function at(v: string | null | undefined): number {
  const n = v ? Date.parse(v) : Number.NaN
  return Number.isNaN(n) ? 0 : n
}

// 進頁即清。`immediate` 是為了「直接用網址進到這一頁／在這一頁重新整理」那條路
// ——它比 onMounted 早跑，所以下面數的時候水位已經是最新的，數出來就是 0（正確）。
watch(
  () => route.name,
  (name) => {
    if (name === 'reseller-requests') markApprovedSeen()
  },
  { immediate: true },
)

/** 側欄「告警」紅徽章＝**未確認**的 crit＋warn（info 不進徽章，免天天亮紅點）。 */
const openAlerts = ref(0)
/** 側欄「任務」徽章＝進行中的派工（queued／leased／running）。 */
const activeJobs = ref(0)

onMounted(async () => {
  // 代理商打 `/api/admin/*` 一律 403（後端 sub-router 結構性隔離）→ 這裡全部略過，
  // 否則登入當下就送出三個註定 403 的請求（console 一排紅字、也白花一輪往返）。
  if (auth.isReseller) {
    try {
      // 批121 §9：草稿／待審核兩個數字改由伺服器算**全表**（以前是載一頁再前端數，
      // 超過一頁就少算）。
      const c = await reseller.myRequestCounts()
      myReqDraft.value = c.draft
      myReqPending.value = c.pending
      // 批108 裁示 15：已核發但還沒看過的筆數（水位存 localStorage，見 readSeenAt）。
      // ★ 這一個**仍然要載列**：判準是 `reviewed_at` 比水位新，計數端點給不了。
      //   只載已核發那個桶（批121 之前是載全部再過濾），近似的範圍因此小了很多。
      const seen = readSeenAt()
      const rows = (await reseller.listRequests({ status: 'approved', limit: 200 })).items
      approvedUnseen.value = rows.filter((r) => at(r.reviewed_at) > seen).length
    } catch {
      myReqDraft.value = 0
      myReqPending.value = 0
      approvedUnseen.value = 0
    }
    return
  }
  // operator 打 /license-requests 會 403（後端 fail-closed）→ 不必發這個請求。
  //
  // 兩個角色看的是**不同的數字**（見 `pendingReq` 的 docstring），所以各自帶不同的
  // 過濾條件打同一支計數端點：manager 以上要「待開通」（不分來源），admin 要「我方
  // 還沒送出的草稿」（`origin=admin`）。
  if (auth.isManagerOrAbove) {
    ls.licenseRequestCounts()
      .then((c) => (staffReq.value = c.pending))
      .catch(() => {})
  } else if (auth.isAdminOrAbove) {
    ls.licenseRequestCounts({ origin: 'admin' })
      .then((c) => (staffReq.value = c.draft))
      .catch(() => {})
  }
  // 批110：帳號清單下放到 manager（他要 onboarding 代理商窗口），徽章跟著放寬。
  if (auth.isManagerOrAbove) {
    try {
      pendingAdmins.value = (await ls.listAdminUsers()).filter((u) => !u.active).length
    } catch {
      pendingAdmins.value = 0
    }
  }
})

// 側欄常駐＝這裡是全 SPA 的告警脈搏（60 秒；告警頁自己另有 30 秒輪詢）。
// 批99-A：沒開放代管產品的帳號直接早退——後端整個 `/hosts` 系 router 有能力閘，
// 不早退的話每 60 秒吃一次 403（console 一直紅字，也白花往返）。
// ★ 批99-B：**代理商不再早退**——`api/hosting.ts::base()` 會把這支打到
//   `/api/admin/reseller/alerts`（只回自家主機的告警），他側欄上的紅點靠它。
usePolling(async () => {
  if (!auth.hostingVisible) return
  try {
    // 批93-D：`limit` 的硬上限降到 200（keyset 分頁的一頁上限）＋回應是 `{items,…}`。
    // 徽章不翻頁：未確認的 crit／warn 一旦多到 200 筆以上，該做的是去告警頁處理，
    // 不是把紅點的數字算得更精確。
    const rows = (await hosting.listHostAlerts({ limit: 200 })).items
    // ★ 2026-09-09：**排除已確認**（`acked_at` 非空）。事件式告警 ack 過之後仍留在未解決
    //   表裡，不濾掉的話紅點會一直亮著，久了就沒有人看它——徽章的價值就歸零了。
    //   與告警頁的預設檢視（隱藏已確認）同一個判準。
    openAlerts.value = rows.filter(
      (a) => !a.acked_at && (a.severity === 'crit' || a.severity === 'warn'),
    ).length
  } catch {
    openAlerts.value = 0
  }
}, 60_000)

// 進行中任務的徽章（30 秒）。後端 `?state=` 只吃單一值，而「進行中」是三個狀態的聯集
// → 拉最近 200 筆自己數（進行中的必然是最新的幾筆，`created_at DESC` 排序保證它們在裡面）。
usePolling(async () => {
  if (!auth.hostingVisible) return
  try {
    const rows = (await hosting.listHostJobs({ limit: 200 })).items
    activeJobs.value = rows.filter((j) => isActiveJobState(j.state)).length
  } catch {
    activeJobs.value = 0
  }
}, 30_000)

/**
 * 批153 批B：〈市集 › 審核〉的徽章＝待審的上架申請＋待審版本。
 *
 * **只給營運主管以上**（與 `pendingReq` 同一個原則：徽章是「這個角色現在該動手的筆數」）——
 * admin 看得到審核頁、能下載包，但核准與婉拒不是他的活，掛一顆他消不掉的紅點只會教人忽略徽章。
 * 審核頁自己載入、核准、婉拒之後也會寫 `lib/reviewBadge`，徽章當場更新。
 */
const isMarketManager = computed(() => canManageMarket(auth.role))
usePolling(async () => {
  if (!isMarketManager.value) return
  try {
    setPendingReviews(reviewQueueCount(await pluginMarket.listReviews()))
  } catch {
    // 讀不到就維持上一個數字（不歸零：短暫 5xx 不該讓紅點閃掉又亮回來）。
  }
}, 60_000)

function logout() {
  auth.logout()
  router.replace({ name: 'login' })
}

/**
 * 側欄三群（批93 §2.2 主人裁示）——**資料驅動**，別再回去手寫十幾個 `<RouterLink>`。
 *
 * 分群的判準是「這件事屬於哪一條線」（批171 起依序：業務 → 報表 → 代管 → 市集 → 產品 → 系統）：
 * - **業務**＝賣證這條線（申請→客戶→證→換機→邀請碼→兌換碼），admin+
 *   （批104 起由「授權」更名——兌換碼是行銷活動，不是一張證）
 * - **代管**＝我方 VM 的維運（部署／主機／派工／告警），operator 的主戰場 → staff 都看得到
 * - **市集**＝擴充套件怎麼發、發給誰（批153；批171 起排在代管下面）
 * - **產品**＝產品與它的登錄資料（憑證設計／指標定義／設定樣板／代管種類）＋外部夥伴（代理商／合作平台）；
 *   **產品頁是 admin 可見**（唯讀）——授權申請頁要靠憑證設計把模組鍵翻成中文。（批171 從「系統」拆出）
 * - **系統**＝帳號、API 金鑰與稽核，營運主管以上。
 *
 * 群標題是小字灰色 section label、不折疊；**群內全部不可見時連標題一起隱藏**
 * （operator 登入只會看到「代管」一群）。徽章欄位對應本檔既有的四個 ref／computed。
 */
type Badge = 'pendingReq' | 'activeJobs' | 'openAlerts' | 'pendingAdmins' | 'approvedUnseen' | 'pendingReviews'
interface NavItem {
  name: string
  label: string
  /**
   * 這一項要掛的徽章（**可以不只一顆**，批108）：代理商的〈授權申請〉同時要顯示
   * 「還有幾筆要處理」（紅）與「已核發幾筆」（綠）——一顆數字表達不了兩件事。
   * 陣列順序＝顯示順序。
   */
  badges?: Badge[]
  /**
   * 可見角色的**精確清單**——與 `router/index.ts` 的 `meta.roles` 同一組值。
   * 批93-C 之前是「最低角色」，代理商（不在階序內）表達不出來，見 `stores/auth.ts`。
   */
  roles: RoleName[]
  /**
   * 批99-A：這一項屬於代管中台，`auth.hostingVisible` 為 false 時隱藏
   * （與 `router/index.ts` 的 `meta.hosting` 一一對應）。**「部署」不掛**——見那邊的註解。
   */
  hosting?: true
}
const STAFF: RoleName[] = ['operator', 'admin', 'manager', 'superadmin']
const ADMIN: RoleName[] = ['admin', 'manager', 'superadmin']
/** 批110：營運主管以上（代理商／帳號管理／稽核紀錄三項）。 */
const MANAGER: RoleName[] = ['manager', 'superadmin']

const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  {
    // 批104 起改叫「業務」：這一群原本只有賣證那條線，兌換碼進來之後「授權」兩個字
    // 已經涵蓋不了（兌換碼是行銷活動，不是一張證）。
    label: '業務',
    items: [
      { name: 'requests', label: '授權申請', badges: ['pendingReq'], roles: ADMIN },
      { name: 'customers', label: '客戶', roles: ADMIN },
      { name: 'licenses', label: '授權', roles: ADMIN },
      { name: 'rebind', label: '換機申請', roles: ADMIN },
      { name: 'invite-codes', label: '邀請碼', roles: ADMIN },
      { name: 'redeem-campaigns', label: '兌換碼', roles: ADMIN },
    ],
  },
  {
    // 批107 項5 新增的一群。目前只有一項，但**現在就分群**是刻意的：報表與「去做一件事」
    // 的頁面是兩種動線（前者是回頭看、後者是往前做），混在「業務」裡會讓那一群愈長愈雜。
    // 日後其他報表（授權、代管用量）也放這裡。
    label: '報表',
    items: [{ name: 'redeem-reports', label: '兌換報表', roles: ADMIN }],
  },
  {
    label: '代管',
    items: [
      { name: 'deployments', label: '部署', roles: STAFF },
      // 開站精靈（/provision）刻意不放 nav：一律從主機詳情進去（要先知道開在哪一台）。
      { name: 'hosts', label: '代管主機', roles: STAFF, hosting: true },
      // 批151 C7：跨主機的站台清單（站台詳情從這裡或主機詳情的站台列進去）。
      { name: 'sites', label: '全部站台', roles: STAFF, hosting: true },
      { name: 'jobs', label: '任務', badges: ['activeJobs'], roles: STAFF, hosting: true },
      { name: 'alerts', label: '告警', badges: ['openAlerts'], roles: STAFF, hosting: true },
    ],
  },
  {
    // 批153：plugin 市集（admin 以上；發行者、公鑰、上下架等寫入在頁內再收到營運主管）。
    // 自成一群：它管的是「擴充套件怎麼發、發給誰」，與賣證（業務）和主機（代管）都不同一條線。
    // 批171（主人裁示）：位置移到「代管」下面。
    label: '市集',
    items: [
      { name: 'plugins', label: '擴充', roles: ADMIN },
      { name: 'plugin-grants', label: '權利', roles: ADMIN },
      { name: 'plugin-publishers', label: '發行者', roles: ADMIN },
      // 批B：第三方上架申請與待審版本（徽章只對營運主管亮，見 `isMarketManager`）。
      { name: 'plugin-reviews', label: '審核', badges: ['pendingReviews'], roles: ADMIN },
    ],
  },
  {
    // 批171（主人裁示）：從「系統」拆出來的一群——產品本身與它的登錄資料（憑證設計、指標詮釋、
    // 主機設定樣板、代管種類）以及替客戶經手授權的外部夥伴（代理商、合作平台）。
    label: '產品',
    items: [
      { name: 'products', label: '產品', roles: ADMIN },
      // 批112：指標顯示定義（我方營運人工維護；與「產品」相鄰但刻意不是它的頁籤
      //   ——產品宣告的是契約，這裡是詮釋，見 `MetricDefsView.vue` 檔頭）。
      { name: 'metric-defs', label: '指標定義', roles: MANAGER },
      // 批112／1.0.44：主機設定的欄位宣告（下發到站台 .env），與「指標定義」相鄰。
      { name: 'agent-config-templates', label: '設定樣板', roles: MANAGER },
      // 批141：代管種類（agent 種類登錄；admin 唯讀、manager 以上可改），與「設定樣板」同屬主機層的登錄資料。
      { name: 'agent-kinds', label: '代管種類', roles: ADMIN },
      { name: 'resellers', label: '代理商', roles: MANAGER },
      // 批141：合作平台（多租戶平台；營運主管以上），與「代理商」並列——兩者都是替客戶
      // 經手授權的外部夥伴，差別在一個是人（窗口帳號）、一個是機器（簽章金鑰）。
      { name: 'platforms', label: '合作平台', roles: MANAGER },
    ],
  },
  {
    label: '系統',
    items: [
      { name: 'admin-users', label: '帳號管理', badges: ['pendingAdmins'], roles: MANAGER },
      // 批147：API 金鑰——**超級管理者專屬**（營運主管以下連選單都看不到，與路由同一組值）。
      { name: 'api-keys', label: 'API 金鑰', roles: ['superadmin'] },
      { name: 'audit', label: '稽核紀錄', roles: MANAGER },
    ],
  },
]

/**
 * 代理商側欄（批93-C 主人裁示）：**不分群、無群標題**——分群的語彙（授權／代管／系統）
 * 是我方的組織圖，對外部夥伴沒有意義。順序＝他的工作動線（先看客戶、再開申請、查證，
 * 然後才是自己那幾台機器）。
 *
 * 批99-B：後三項是他自建的代管主機（`hosting: true`）——範圖裡沒有開放代管的產品時
 * 整組消失（後端 `admin_reseller_hosting.router` 同一道 `require_hosting_scope`）。
 * ★ 開站精靈（`reseller-provision`）刻意不放 nav，與我方那邊一致：一律從主機詳情進去
 *   （要先知道開在哪一台）。
 */
const RESELLER_NAV: NavItem[] = [
  { name: 'reseller-customers', label: '我的客戶', roles: ['reseller'] },
  {
    name: 'reseller-requests',
    label: '授權申請',
    badges: ['pendingReq', 'approvedUnseen'],
    roles: ['reseller'],
  },
  { name: 'reseller-licenses', label: '授權', roles: ['reseller'] },
  // 批104：兌換碼。★ **不帶 `hosting`**——兌換碼屬業務線，代管能力閘關掉時仍要看得到
  //   （他還是得發自家活動的碼）。
  { name: 'reseller-redeem-campaigns', label: '兌換碼', roles: ['reseller'] },
  // 批107 項5：兌換報表。代理商側欄**不分群**（分群的語彙是我方的組織圖），所以不另立
  // 「報表」群，直接排在「兌換碼」後面——那正好是他的動線（發完碼就想看兌得如何）。
  { name: 'reseller-redeem-reports', label: '兌換報表', roles: ['reseller'] },
  { name: 'reseller-hosts', label: '我的主機', roles: ['reseller'], hosting: true },
  { name: 'reseller-jobs', label: '任務', badges: ['activeJobs'], roles: ['reseller'], hosting: true },
  {
    name: 'reseller-alerts',
    label: '告警',
    badges: ['openAlerts'],
    roles: ['reseller'],
    hosting: true,
  },
]
/** 代理商側欄的可見項目（`canSee` 同時管角色與代管能力閘）。 */
const visibleResellerNav = computed(() => RESELLER_NAV.filter(canSee))

const badgeCounts = computed<Record<Badge, number>>(() => ({
  pendingReq: pendingReq.value,
  activeJobs: activeJobs.value,
  openAlerts: openAlerts.value,
  pendingAdmins: pendingAdmins.value,
  approvedUnseen: approvedUnseen.value,
  pendingReviews: isMarketManager.value ? pendingReviews.value : 0,
}))
/**
 * 「任務」的徽章是中性藍（進行中不是壞事）、「已核發」是綠（好消息），
 * 其餘都是要人處理的紅點。
 */
function badgeClass(b: Badge): string {
  if (b === 'activeJobs') return 'badge info'
  if (b === 'approvedUnseen') return 'badge ok'
  return 'badge'
}
/** 滑過徽章時的說明——同一個位置擠兩顆數字，沒有文字沒人分得出誰是誰。 */
const BADGE_TITLES: Record<Badge, string> = {
  pendingReq: '待處理的申請（草稿＋審核中）',
  activeJobs: '進行中的任務',
  openAlerts: '未確認的告警',
  pendingAdmins: '待啟用的帳號',
  approvedUnseen: '上次看過之後新核發的申請，進頁即清',
  pendingReviews: '待審的上架申請與版本',
}
function canSee(item: NavItem): boolean {
  if (!auth.role || !(item.roles as string[]).includes(auth.role)) return false
  // 批99-A：代管項目再過一道能力閘（後端同一道，這裡只是不給看得到卻按不動的入口）。
  return !item.hosting || auth.hostingVisible
}
/**
 * 群標題的動態改寫（批99-A，主人裁示 A-A）：「代管」群把代管三項濾掉之後只剩「部署」，
 * 這時標題改成「**監控**」——一個代管功能都看不到的人，側欄上卻掛著「代管」兩字，
 * 讀起來像功能壞掉。判準刻意是「這一群**原本**有 hosting 項而現在一個都不剩」而不是
 * 寫死群名，之後新增代管頁自動跟著走。
 */
function groupLabel(g: { label: string; items: NavItem[] }, visible: NavItem[]): string {
  if (g.label !== '代管') return g.label
  const hadHosting = g.items.some((i) => i.hosting)
  return hadHosting && !visible.some((i) => i.hosting) ? '監控' : g.label
}
const visibleGroups = computed(() =>
  NAV_GROUPS.map((g) => {
    const items = g.items.filter(canSee)
    return { label: groupLabel(g, items), items }
  }).filter((g) => g.items.length > 0),
)

// --- 手機／平板（<1200）：頂欄＋漢堡抽屜（批171）----------------------------------
//
// 240px 的固定側欄在平板 820 寬只剩約 580px 給內容、手機只剩約 150px——所有清單與表單都被擠爛。
// <1200（`isCompact`，與 CSS 的 `@media (--compact)` 同一條查詢）改成頂欄＋覆蓋式抽屜，
// 做法照 portal 的 `AppShell.vue`：換頁自動收、點遮罩或按 Esc 關、切回桌機時歸零。
// 桌機（≥1200）的 DOM 與樣式完全不變。
const drawerOpen = ref(false)
/** 點了選單項目就收起來（抽屜蓋住整個內容，不收等於點了沒反應）。 */
watch(
  () => route.fullPath,
  () => {
    drawerOpen.value = false
  },
)
// 轉回桌機寬度時把抽屜狀態歸零，免得再縮回平板時它自己是開的。
watch(isCompact, (c) => {
  if (!c) drawerOpen.value = false
})
const backdropClose = useBackdropClose(() => {
  drawerOpen.value = false
})
/** 桌機恆顯示；手機／平板只有抽屜打開時才掛（關著就不在 DOM，不會有看不見卻可 Tab 到的連結）。 */
const showSidebar = computed(() => !isCompact.value || drawerOpen.value)

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && drawerOpen.value) drawerOpen.value = false
}
onMounted(() => window.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))

/**
 * 漢堡鈕上的徽章：側欄**看得到的項目**上所有徽章的總數（抽屜收著時待辦不能跟著被藏起來）。
 * 顏色取最急的那一種——有任何紅色（要人處理）就是紅，否則進行中任務是藍、只有「已核發」是綠。
 * 只數看得到的項目：徽章本來就只掛在看得到的項目上，這樣跟抽屜裡加起來的數字一致。
 */
const burgerBadge = computed<{ count: number; cls: string; title: string }>(() => {
  const items = auth.isReseller ? visibleResellerNav.value : visibleGroups.value.flatMap((g) => g.items)
  let count = 0
  let urgent = false
  let info = false
  const parts: string[] = []
  for (const item of items) {
    for (const b of item.badges ?? []) {
      const n = badgeCounts.value[b]
      if (!n) continue
      count += n
      parts.push(`${item.label} ${n}`)
      if (b === 'activeJobs') info = true
      else if (b !== 'approvedUnseen') urgent = true
    }
  }
  const cls = urgent ? 'badge' : info ? 'badge info' : 'badge ok'
  return { count, cls, title: parts.join('、') }
})
</script>

<template>
  <div class="shell" :class="{ compact: isCompact }">
    <!-- 手機／平板頂欄（批171）：漢堡＋品牌。桌機不掛——品牌在側欄裡。 -->
    <header v-if="isCompact" class="mtop">
      <button
        class="burger"
        :aria-label="burgerBadge.count ? `開啟選單（${burgerBadge.title}）` : '開啟選單'"
        :aria-expanded="drawerOpen"
        @click="drawerOpen = true"
      >
        <span /><span /><span />
        <i v-if="burgerBadge.count" :class="['burger-badge', burgerBadge.cls]" :title="burgerBadge.title">
          {{ burgerBadge.count > 99 ? '99+' : burgerBadge.count }}
        </i>
      </button>
      <img :src="logoSrc" alt="iPlusOne" class="mlogo" />
      <span class="mtitle">License Server</span>
      <span class="spacer" />
      <ThemeToggle />
    </header>

    <div v-if="isCompact && drawerOpen" class="drawer-backdrop" v-on="backdropClose" />

    <aside v-if="showSidebar" class="sidebar" :class="{ drawer: isCompact }">
      <div class="brand">
        <img :src="logoSrc" alt="iPlusOne" class="logo" />
        <div class="brand-text">
          <span class="b1">License Server</span>
          <span class="b2">愛+1 內部後台</span>
        </div>
      </div>

      <!-- 代理商（批93-C）：三項、不分群。刻意走另一份清單而不是「三群都過濾一遍」——
           分群的語彙（授權／代管／系統）是我方的組織圖，對外部夥伴沒有意義。 -->
      <nav v-if="auth.isReseller" class="nav">
        <div class="nav-org">{{ auth.resellerName ?? '代理商' }}</div>
        <RouterLink
          v-for="item in visibleResellerNav"
          :key="item.name"
          class="nav-item"
          active-class="active"
          :to="{ name: item.name }"
        >
          <span>{{ item.label }}</span>
          <template v-for="b in item.badges ?? []" :key="b">
            <span v-if="badgeCounts[b]" :class="badgeClass(b)" :title="BADGE_TITLES[b]">
              {{ badgeCounts[b] }}
            </span>
          </template>
        </RouterLink>
      </nav>

      <!-- 三群（批93 §2.2 主人裁示）：授權／代管／系統。可見性沿 router 的 meta.roles，
           群內全部不可見時連群標題一起隱藏——operator 只會看到「代管」那一群。 -->
      <nav v-else class="nav">
        <!-- 每群包一層 `.nav-group`（批93 分界修正）：分隔線與群間距掛在**容器**上，
             用 `.nav-group + .nav-group` 的相鄰選擇器畫線——首群天生沒有前一個兄弟，
             不必再靠 `:first-child` 特例排除，`visibleGroups` 濾掉空群後也不會留下孤兒線。 -->
        <div v-for="g in visibleGroups" :key="g.label" class="nav-group">
          <div class="nav-group-label">{{ g.label }}</div>
          <RouterLink
            v-for="item in g.items"
            :key="item.name"
            class="nav-item"
            active-class="active"
            :to="{ name: item.name }"
          >
            <span>{{ item.label }}</span>
            <template v-for="b in item.badges ?? []" :key="b">
              <span v-if="badgeCounts[b]" :class="badgeClass(b)" :title="BADGE_TITLES[b]">
                {{ badgeCounts[b] }}
              </span>
            </template>
          </RouterLink>
        </div>
      </nav>

      <div class="foot">
        <ThemeToggle v-if="!isCompact" />
        <button class="logout" @click="logout">登出</button>
      </div>
    </aside>

    <main class="main">
      <RouterView />
    </main>
  </div>
</template>

<style scoped>
.shell {
  display: grid;
  grid-template-columns: 240px 1fr;
  /* ★★ 批110：**固定視窗高**，不是 `min-height`。
     原本 `min-height: 100vh` 的 grid row 會跟著內容長高，而 `.sidebar` 沒有任何高度宣告
     → grid 的 `stretch` 讓它等於那個 row 的高度 → 授權頁載入 200 筆時側欄變成好幾千 px、
     登出鈕被推到文件最底、整條導覽捲出視窗。`.main` 雖然早就有 `overflow: auto`，但沒有
     高度上界時它永遠不會真的內捲（在捲的是 document）。
     `100dvh` 讓行動瀏覽器的網址列收合時不會多出一截；`100vh` 留著當不支援 dvh 的 fallback。
     ★ portal 的 `portal/src/views/AppShell.vue` 是這份的副本（10-07 才補上同一個修法）——改一邊要想到另一邊。 */
  height: 100vh;
  height: 100dvh;
}
.sidebar {
  display: flex;
  flex-direction: column;
  /* flex 子項的預設 `min-height: auto` 會讓下面的 `.nav` 撐破側欄（內容多時又長回去）。 */
  min-height: 0;
  background: var(--bg-panel);
  border-right: 1px solid var(--border);
  padding: var(--sp-3);
}
.brand {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  padding: var(--sp-2) var(--sp-2) var(--sp-4);
}
.logo {
  height: 32px;
  width: auto;
  object-fit: contain;
}
.brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}
.b1 {
  font-size: var(--fs-14);
  font-weight: 700;
  color: var(--fg);
}
.b2 {
  font-size: var(--fs-11);
  color: var(--fg-3);
}
.nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  /* 導覽項目多過視窗高度時（小筆電＋代管群全開）自己捲，不去推擠下面的登出列。 */
  min-height: 0;
  overflow-y: auto;
}
/*
 * 群與群的分界（主人 2026-09-09 回饋：「左邊選單沒有很清楚的分類分界」）。
 * 做法＝**細分隔線＋加大群間距**；線用 `--border`（**不是** `--border-2`）——
 * 深色主題下 `--border-2` (#172036) 跟側欄底 `--bg-panel` (#0f172a) 差不到一個色階，
 * 畫了等於沒畫；`--border` 兩個主題都看得見。
 * 首群不畫線也不加上邊距（避免頂到 brand 區）——相鄰選擇器天生就排除它。
 */
.nav-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.nav-group + .nav-group {
  margin-top: var(--sp-4);
  padding-top: var(--sp-3);
  border-top: 1px solid var(--border);
}
/*
 * 群標題沿用設計稿的 section label 配方（`docs/ux_reviews/20260428_原始設計交接稿/styles/app.css`
 * 的 `.sub-nav-section`：小字、**700 字重**、`--fg-3`、拉開字距），字級換成 LS 自己的
 * `--fs-11`（設計稿寫死 10px，LS 的 type scale 整體大一級）。`text-transform: uppercase`
 * 對中文標題是 no-op，就不搬了；字距改 0.08em 換取中文雙字詞的辨識度。
 * 刻意維持 `--fg-3` 小字——分界交給分隔線去做，標題不能搶過選單項目本身。
 */
.nav-group-label {
  padding: 0 10px 4px;
  font-size: var(--fs-11);
  color: var(--fg-3);
  font-weight: 700;
  letter-spacing: 0.08em;
}
/* 代理商側欄頂部的公司名（取代群標題——他只有一個「群」，就是他自己）。 */
.nav-org {
  padding: 0 10px 6px;
  font-size: var(--fs-12);
  color: var(--fg-3);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nav-item {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  padding: 8px 10px;
  border-radius: var(--r-sm);
  color: var(--fg-2);
  font-size: var(--fs-13);
  font-weight: 500;
  text-decoration: none;
  transition: all var(--dur-fast) var(--ease);
}
.nav-item:hover {
  background: var(--bg-hover);
  color: var(--fg);
}
.nav-item.active {
  background: var(--brand-100);
  color: var(--brand-700);
  font-weight: 600;
}
[data-theme='dark'] .nav-item.active {
  background: var(--bg-active);
  color: var(--brand-400);
}
/* `.badge` 已提升到 styles/ui.css（告警頁與主機清單共用同一顆徽章）。
   這裡只補「同一列兩顆」的間距——`.badge` 本身是 `margin-left: auto`（把自己推到最右），
   第二顆再吃一次 auto 會把兩顆拆到兩端，所以第二顆起改成固定間距。 */
.nav-item .badge + .badge {
  margin-left: 4px;
}
.foot {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  padding-top: var(--sp-3);
  border-top: 1px solid var(--border-2);
}
.logout {
  flex: 1;
  height: var(--control-h);
  border: 1px solid var(--border-strong);
  border-radius: var(--r-sm);
  background: var(--bg-panel);
  color: var(--fg-2);
  font-family: inherit;
  font-size: var(--fs-13);
  cursor: pointer;
}
.logout:hover {
  background: var(--bg-hover);
}
.main {
  min-width: 0;
  overflow: auto;
  padding: var(--sp-6);
}

/* --- 手機／平板（<1200）：頂欄＋漢堡抽屜（批171）-------------------------------------
 * 全部掛在 `.shell.compact`（只有 `isCompact` 時才有這個 class）或 `@media (--compact)` 底下，
 * 桌機逐像素不變。仍維持「固定視窗高、`.main` 自己內捲」——頂欄因此一直在畫面上，
 * 抽屜與徽章隨時按得到。
 * 安全區（橫放瀏海）：`env(safe-area-inset-*)` 只有 index.html 開 `viewport-fit=cover` 才有值；
 * 沒開時 Safari 自己把內容讓出瀏海區，這裡的 max()／calc() 等於原值，開了之後自動生效。 */
.shell.compact {
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
}
.mtop {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  padding: var(--sp-2) max(var(--sp-3), env(safe-area-inset-right)) var(--sp-2) max(var(--sp-3), env(safe-area-inset-left));
  background: var(--bg-panel);
  border-bottom: 1px solid var(--border);
}
.mtop .spacer {
  flex: 1;
}
.mlogo {
  height: 24px;
  width: auto;
  object-fit: contain;
}
.mtitle {
  font-size: var(--fs-13);
  font-weight: 700;
  color: var(--fg);
  white-space: nowrap;
}
.burger {
  position: relative;
  display: grid;
  gap: 4px;
  width: var(--control-h);
  height: var(--control-h);
  padding: 9px 8px;
  align-content: center;
  border: 1px solid var(--border-strong);
  border-radius: var(--r-sm);
  background: var(--bg-panel);
  cursor: pointer;
  flex: none;
}
.burger > span {
  display: block;
  height: 2px;
  background: var(--fg-2);
  border-radius: 1px;
}
/* 漢堡鈕右上角的徽章（沿用共用 `.badge` 的顏色與字級；位置改成角標）。 */
.burger .burger-badge {
  position: absolute;
  top: -7px;
  right: -9px;
  margin: 0;
  font-style: normal;
  box-shadow: 0 0 0 2px var(--bg-panel);
}
/* 抽屜：蓋在內容上（遮罩 1000、抽屜本體 1001；比 DialogsHost 的 1100 低——對話框開著時抽屜不該蓋過它）。 */
.sidebar.drawer {
  position: fixed;
  z-index: 1001;
  top: 0;
  left: 0;
  bottom: 0;
  width: min(260px, 82vw);
  padding-left: calc(var(--sp-3) + env(safe-area-inset-left));
  box-shadow: var(--shadow-lg);
}
.drawer-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(15, 23, 42, 0.45);
}
@media (--compact) {
  .main {
    padding: var(--sp-5) max(var(--sp-5), env(safe-area-inset-right)) var(--sp-5) max(var(--sp-5), env(safe-area-inset-left));
  }
}
@media (--mobile) {
  .main {
    padding: var(--sp-3) max(var(--sp-3), env(safe-area-inset-right)) var(--sp-4) max(var(--sp-3), env(safe-area-inset-left));
  }
}
</style>
